import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const RULES = [
  ["Unauthorized use of Mess facility by Day Scholar/ other Mess Student.", "Monetary fine of Rs 500/- on Day Scholar and Rs 250/- on Student of other Mess."],
  ["Keeping Day Scholar in Hostel Room during Night.", "Monetary fine Rs 5000/- on both Hosteller and Day Scholar."],
  ["Intentionally damaging property and equipment of the hostel, bathroom, common room, gym, CCTV camera etc.", "Monetary fine three times the actual cost of the damaged equipment."],
  ["Attempting to harm oneself or causing injury to other hostel inmates.", "Immediate expulsion from hostel. No hostel facility in future."],
  ["Keeping, using or supplying intoxicants in any form (ganja, opium, liquor, contraband etc.) and drug abuse.", "Immediate expulsion from hostel."],
  ["Gambling in any form.", "First time monetary fine of Rs 1000/-; if caught again, expulsion from hostel."],
  ["Bullying, threatening or harassing a student in any form.", "Immediate expulsion from hostel. No hostel facility in future."],
  ["Physically assaulting an inmate or staff, including use of belt during birthday/placement celebrations.", "Expulsion from hostel for one semester."],
  ["Demonstration or inciting in any form including procession, meeting or hunger strike.", "Expulsion from hostel for two weeks."],
  ["Disturbing other students in their studies.", "Counselling and undertaking the first time. Second time, guardian called. Third time, expulsion from hostel for three months."],
  ["Not putting biometric attendance.", "Auto SMS to parents after 10:30 PM. If continued for three days, monetary fine of Rs 100/- per day from day four."],
  ["Absent from hostel without granting leave or late entry from outside.", "Full diet charge to be taken for the absence period even after confirmation of leave from parents."],
  ["Displaying unauthorized posters, notices, leaflets or writing unsocial/obscene comments on walls, doors or almirahs.", "Counselling and call to guardians. Monetary fine double the cost of painting/cleaning."],
  ["Use of electrical appliances like heater, room heater, immersion rod etc.", "Immediate seizure of item and monetary fine of Rs 100/-."],
  ["Theft of mobile, wallet, laptop, shoes, costly/branded items etc.", "Immediate expulsion from hostel if proved, plus repayment."],
  ["Taking mess utensils or cooking in the room.", "Monetary fine of Rs 500/- added to the mess bill of the month."],
  ["Spitting on walls after chewing gutka, pan masala or smoking.", "Monetary fine of Rs 500/- added to the mess bill of the month (each time)."],
  ["Using a table/chair or any other item instead of a proper stand for the desert cooler.", "Instructions to use proper stand; otherwise monetary fine of Rs 1000/-."],
  ["Consumption of liquor.", "For first year: expulsion from hostel. For later years: fine of Rs 5000/- (first time), Rs 10000/- (second time); on repetition, expulsion from hostel."],
  ["Misuse of social media.", "If misuse leads to large-scale indiscipline, immediate expulsion. Other cases, action based on gravity."],
  ["Any visitor (alumni, guardian etc.) reporting at the hostel after 8:00 PM.", "Security guard will arrange and manage stay in campus, if possible, as per warden."],
  ["No discount and refund will be entertained."],
  ["The security deposit is non-refundable."],
  ["The room must be vacated before the commencement of the summer vacation."]
];

export default function StudentConsent() {
  const { refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  async function accept() {
    if (!checked) return;
    setSaving(true);
    try {
      await api.post("/students/me/accept-tnc");
      await refresh();
      toast.success("Thanks — terms accepted");
      navigate("/student", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save acceptance");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream py-10 px-4">
      <div className="max-w-4xl mx-auto bg-white border border-warm rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-warm flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-warm">First-time login</div>
            <h1 className="font-heading text-3xl text-deep mt-1">Code of Conduct &amp; Discipline</h1>
            <p className="text-sm text-warm mt-1">Please read carefully and accept before you continue.</p>
          </div>
          <button onClick={logout} className="text-sm text-warm hover:text-deep underline">Sign out</button>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          <ol className="space-y-4 text-sm text-deep">
            {RULES.map(([act, punishment], i) => (
              <li key={i} className="border-b border-warm/60 pb-3 last:border-0">
                <div className="font-medium">{i + 1}. {act}</div>
                <div className="text-warm mt-1"><span className="font-medium text-deep">Action: </span>{punishment}</div>
              </li>
            ))}
          </ol>
          <p className="text-xs text-warm mt-6">
            Note: Use of desert cooler is permitted with a proper cooler stand only. By accepting, you confirm you have read and will abide by the rules above.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-warm bg-[#FDFBF7]">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#C86F58]"
              data-testid="consent-checkbox"
            />
            <span className="text-sm text-deep">
              I have read and agree to abide by the Code of Conduct &amp; Discipline above.
            </span>
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={logout}
              type="button"
              className="px-5 py-2 rounded-md text-sm border border-warm text-deep hover:bg-[#F4F1EA]"
            >
              Decline &amp; sign out
            </button>
            <button
              onClick={accept}
              disabled={!checked || saving}
              data-testid="accept-tnc-btn"
              className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "I accept"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
